import React, { useEffect, useState } from 'react';
import './AffiliateMarketingGrid.scss';
import { http, localstorage, useNavigationService, general, AlertService } from '../../fx-core';
import { makeStyles, Breadcrumbs, Typography, Button, TextField, Checkbox, Card, CardContent, Dialog, DialogTitle, DialogContent, DialogActions, FormControlLabel, Popover } from '@material-ui/core';
import { NoRecords } from '../../NoRecords/NoRecords';

const useStyles = makeStyles((theme) => ({
  marginInput: {
    width: '100%',
    '& .MuiOutlinedInput-input': { padding: '6px 10px', fontSize: '13px' },
    '& .MuiOutlinedInput-root': { borderRadius: '4px' }
  }
}));

const ITEMS_PER_PAGE = 8;

const AffiliateMarketingGrid = (props: any) => {
  const { navigate } = useNavigationService();
  const classes = useStyles();
  const currentUserId = parseInt(localstorage.getItem('userId') || '0');

  const [allItems, setAllItems] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState('all');

  const [promoteDialogOpen, setPromoteDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [commissionChoice, setCommissionChoice] = useState<'default' | 'custom'>('default');
  const [customCommission, setCustomCommission] = useState('');
  const [promoteSubmitting, setPromoteSubmitting] = useState(false);
  const [promotedIds, setPromotedIds] = useState<number[]>([]);

     const [marginRangeFilter, setMarginRangeFilter] = useState<string>('all');
  const [marginRangeAnchorEl, setMarginRangeAnchorEl] = useState<null | HTMLElement>(null);
  const [productFilter, setProductFilter] = useState<'recommended' | 'all' | 'mine'>(() => {
    const incoming = sessionStorage.getItem('affiliateProductFilter');
    if (incoming === 'mine') {
      sessionStorage.removeItem('affiliateProductFilter');
      return 'mine';
    }
    return 'all';
  });
const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);
const [myPostedItems, setMyPostedItems] = useState<any[]>([]);

  const [stockPopupAnchor, setStockPopupAnchor] = useState<null | HTMLElement>(null);
  const [stockInfo, setStockInfo] = useState<any>(null);

  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [viewerImage, setViewerImage] = useState('');
  const [zoomLevel, setZoomLevel] = useState(1);

   const [previewItem, setPreviewItem] = useState<any>(null);

  const getStallName = (item: any) =>
    item.stallName ?? item.StallName ?? item.merchantStallName ?? item.MerchantStallName ?? item.stall?.name ?? '';
  const getCategoryName = (item: any) => {
    const direct = item.categoryName ?? item.CategoryName ?? item.category ?? item.Category;
    if (direct && typeof direct === 'string') return direct;
    const cats = item.categories ?? item.Categories;
    if (Array.isArray(cats) && cats.length > 0) {
      return cats.map((c: any) => c.name ?? c.Name ?? c.categoryName ?? c.CategoryName).filter(Boolean).join(', ');
    }
    return '';
  };
  const getCategoryNames = (item: any): string[] =>
    getCategoryName(item)
      .split(',')
      .map((name: string) => name.trim())
      .filter(Boolean);

  const categoryOptions = React.useMemo(() => {
    const names = new Map<string, string>();
    allItems.forEach((item: any) => {
      getCategoryNames(item).forEach((name: string) => {
        const key = name.toLowerCase();
        if (!names.has(key)) names.set(key, name);
      });
    });
    return Array.from(names.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [allItems]);


     const getStallSlug = (item: any) => item.slug ?? item.Slug ?? item.stallSlug ?? item.StallSlug ?? '';

  const handleStockClick = (e: React.MouseEvent<HTMLElement>, stallItemId: number) => {
    setStockPopupAnchor(e.currentTarget);
    http.get({ url: `stallitem/${stallItemId}/affiliate-stock` }).then((res: any) => {
      setStockInfo(res.data);
    }).catch((err: any) => {
      console.error('Failed to fetch stock info:', err);
    });
  };

    const fetchAllAffiliateProducts = () => {
    http.get({ url: 'stallitem/affiliate/all' }).then(res => {
      const list = res.data || [];
      const mine = list.filter((item: any) => {
        const ownerId = item.ownerId ?? item.OwnerId ?? 0;
        return ownerId === currentUserId;
      });
      setAllItems(list);       // All Products now includes everyone, including you
      setMyPostedItems(mine);  // My Products still works as its own filter
      setCurrentPage(1);
    });
  };

  const [myRequestsByItemId, setMyRequestsByItemId] = useState<Record<string, any>>({});

  const fetchMyRequests = () => {
    if (!currentUserId) return;
    http.get({ url: `affiliate-product/reseller/${currentUserId}` }).then((res: any) => {
      const list = res.data || [];
      const map: Record<string, any> = {};
      list.forEach((r: any) => {
        const key = String(r.itemId ?? r.ItemId ?? '');
        if (!key) return;
        const existing = map[key];
        const rDate = new Date(r.createdDate || r.CreatedDate || 0);
        const existingDate = existing ? new Date(existing.createdDate || existing.CreatedDate || 0) : null;
        if (!existing || rDate > existingDate) {
          map[key] = r;
        }
      });
      setMyRequestsByItemId(map);
    }).catch(() => setMyRequestsByItemId({}));
  };

  useEffect(() => {
    fetchAllAffiliateProducts();
    fetchMyRequests();
  }, []);

  const openPromoteDialog = (item: any) => {
    setSelectedItem(item);
    setCommissionChoice('default');
    setCustomCommission('');
    setPromoteDialogOpen(true);
  };

  const closePromoteDialog = () => {
    setPromoteDialogOpen(false);
    setSelectedItem(null);
  };

  const submitPromotion = () => {
    if (!selectedItem) return;
    const itemOwnerId = selectedItem.ownerId ?? selectedItem.OwnerId ?? 0;
    if (itemOwnerId === currentUserId) {
      AlertService.showErrorMsg && AlertService.showErrorMsg('You cannot promote your own product.');
      return;
    }
    const merchantMargin = Number(selectedItem.marginNumber ?? selectedItem.MarginNumber) || 0;
    const marginValue = commissionChoice === 'default' ? merchantMargin : Number(customCommission);

        if (commissionChoice === 'custom' && (!customCommission || isNaN(marginValue) || marginValue <= 0 || marginValue > 100)) {
      AlertService.showErrorMsg && AlertService.showErrorMsg('Please enter a valid commission percentage between 1 and 100.');
      return;
    }

    setPromoteSubmitting(true);
    const itemId = selectedItem.id ?? selectedItem.Id ?? selectedItem.stallItemId ?? selectedItem.StallItemId;
    const payload = [{
      ResellerId: parseInt(localstorage.getItem('userId') || '0'),
      MerchantId: selectedItem.ownerId ?? selectedItem.OwnerId ?? 0,
      MerchantStallId: selectedItem.stallId ?? selectedItem.StallId ?? 0,
      ResellerStallId: 0,
      ItemId: itemId,
      ItemName: selectedItem.itemName ?? selectedItem.ItemName,
      MarginNumber: marginValue
    }];

        http.post({ url: 'affiliate-product/addAffiliateProduct', data: payload }).then(() => {
      setPromoteSubmitting(false);
      AlertService.showSuccessMsg('Your request has been sent to the product owner.');
      window.dispatchEvent(new Event('refreshNotifications'));
      fetchMyRequests();
      closePromoteDialog();
    }).catch((err: any) => {
      console.error('Promotion submit failed:', err);
      setPromoteSubmitting(false);
      // The app's http wrapper already surfaces the server's error message as a toast —
      // no need to show a second, generic one here.
    });
  };
const getMargin = (item: any) => Number(item.marginNumber ?? item.MarginNumber ?? 0);
const getOwnerId = (item: any) => item.ownerId ?? item.OwnerId ?? 0;

const MARGIN_RANGES: { key: string; label: string; min: number; max: number }[] = [
  { key: 'all', label: 'All Commissions', min: 0, max: Infinity },
  { key: '0-20', label: '0% – 20%', min: 0, max: 20 },
  { key: '20-30', label: '20% – 30%', min: 20, max: 30 },
  { key: '30-40', label: '30% – 40%', min: 30, max: 40 },
  { key: '40-50', label: '40% – 50%', min: 40, max: 50 },
  { key: '50-60', label: '50% – 60%', min: 50, max: 60 },
  { key: '60-70', label: '60% – 70%', min: 60, max: 70 },
  { key: '70-80', label: '70% – 80%', min: 70, max: 80 },
  { key: '80-100', label: '80% – 100%', min: 80, max: 100 },
];

const sourceItems = React.useMemo(() => {
  if (productFilter === 'mine') return myPostedItems;
  if (productFilter === 'recommended') {
    let othersOnly = allItems.filter((item: any) => getOwnerId(item) !== currentUserId);
    if (selectedCategory !== 'all') {
      othersOnly = othersOnly.filter((item: any) =>
        getCategoryNames(item).some(
          (name: string) => name.toLowerCase() === selectedCategory
        )
      );
    }


    const range = MARGIN_RANGES.find(r => r.key === marginRangeFilter);
    if (range && range.key !== 'all') {
      othersOnly = othersOnly.filter((item: any) => {
        const m = getMargin(item);
        return m >= range.min && m <= range.max;
      });
    }

    return [...othersOnly].sort((a, b) => getMargin(b) - getMargin(a));
  }
  return allItems;
}, [productFilter, allItems, myPostedItems, currentUserId, marginRangeFilter, selectedCategory]);

const totalPages = Math.ceil(sourceItems.length / ITEMS_PER_PAGE) || 1;
const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
const currentItems = sourceItems.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  return (
    <div className="affiliate-marketing-grid-ui" style={{ paddingTop: '90px' }}>
      {/* Breadcrumb Strip */}
<div className="title-font custom-head">
  <Breadcrumbs aria-label="breadcrumb">
    <span
      className="custom-link pointer breadcrumbs-style"
      onClick={() => navigate({ targetRoute: '', targetTitle: 'Dashboard', targetData: {}, sourceState: {}, eventSource: '' })}
    >
      <b>Dashboard</b>
    </span>
    <Typography color="textPrimary" className="breadcrumbs-style">Affiliated Products</Typography>
  </Breadcrumbs>
</div>

<div className="affiliate-section-tabs px-4" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 10, paddingBottom: 10, flexWrap: 'wrap', gap: 10 }}>
  <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
    <div style={{ display: 'flex', gap: 8 }}>
      <Button
        size="small"
        variant="contained"
        disableElevation
        style={{ background: '#ff6f00', color: '#fff', textTransform: 'none' }}
      >
        Products
      </Button>
      <Button
        size="small"
        variant="outlined"
        disableElevation
        style={{ textTransform: 'none' }}
        onClick={() => navigate({ targetRoute: '/provider/affiliatedashboard', targetTitle: 'Affiliate Dashboard', targetData: {}, sourceState: {}, eventSource: '' })}
      >
        Analytics
      </Button>
    </div>

    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <div
        className="pointer"
        onClick={(e) => setFilterAnchorEl(e.currentTarget)}
        title="Filter products"
        style={{
          width: 34, height: 34, borderRadius: '50%',
          border: '1px solid #e0e0e0', background: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#ff6f00', cursor: 'pointer'
        }}
      >
        <i className="fas fa-filter"></i>
      </div>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#263238' }}>
        {productFilter === 'recommended' && 'Recommended For You'}
        {productFilter === 'all' && 'All Products'}
        {productFilter === 'mine' && `My Products (${myPostedItems.length})`}
      </span>

      {productFilter === 'recommended' && (
        <>
          <select
            aria-label="Filter affiliate products by category"
            value={selectedCategory}
            onChange={(e) => {
              setSelectedCategory(e.target.value);
              setCurrentPage(1);
            }}
            style={{
              maxWidth: '100%',
              minHeight: 32,
              padding: '4px 10px',
              border: '1px solid #ffd3a6',
              borderRadius: 14,
              background: '#fff',
              color: '#b85e00',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            <option value="all">All Categories</option>
            {categoryOptions.map((category) => (
              <option key={category.value} value={category.value}>
                {category.label}
              </option>
            ))}
          </select>

          <div
            className="pointer"
            onClick={(e) => setMarginRangeAnchorEl(e.currentTarget)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              border: '1px solid #ffd3a6', borderRadius: 14, padding: '3px 10px',
              fontSize: 12, fontWeight: 600, color: '#ff6f00', cursor: 'pointer'
            }}
          >
            <i className="fas fa-percent" style={{ fontSize: 10 }}></i>
            {MARGIN_RANGES.find(r => r.key === marginRangeFilter)?.label || 'All Commissions'}
            <i className="fas fa-chevron-down" style={{ fontSize: 9 }}></i>
          </div>

          <Popover
            open={Boolean(marginRangeAnchorEl)}
            anchorEl={marginRangeAnchorEl}
            onClose={() => setMarginRangeAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          >
            <div style={{ minWidth: 170, padding: 6 }}>
              {MARGIN_RANGES.map((r) => (
                <div
                  key={r.key}
                  className="pointer"
                  onClick={() => { setMarginRangeFilter(r.key); setCurrentPage(1); setMarginRangeAnchorEl(null); }}
                  style={{
                    padding: '8px 12px', borderRadius: 6, fontSize: 13,
                    fontWeight: marginRangeFilter === r.key ? 700 : 500,
                    color: marginRangeFilter === r.key ? '#ff6f00' : '#444',
                    background: marginRangeFilter === r.key ? '#fff3e0' : 'transparent'
                  }}
                >
                  {r.label}
                </div>
              ))}
            </div>
          </Popover>
        </>
      )}

      <Popover
        open={Boolean(filterAnchorEl)}
        anchorEl={filterAnchorEl}
        onClose={() => setFilterAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <div style={{ minWidth: 200, padding: 6 }}>
          {[
            { key: 'recommended', label: 'Recommended For You', icon: 'fa-star' },
            { key: 'all', label: 'All Products', icon: 'fa-th-large' },
            { key: 'mine', label: `My Products (${myPostedItems.length})`, icon: 'fa-user' },
          ].map((opt) => (
            <div
              key={opt.key}
              className="pointer"
              onClick={() => { setProductFilter(opt.key as any); setMarginRangeFilter('all'); setCurrentPage(1); setFilterAnchorEl(null); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 12px', borderRadius: 6, fontSize: 13,
                fontWeight: productFilter === opt.key ? 700 : 500,
                color: productFilter === opt.key ? '#ff6f00' : '#444',
                background: productFilter === opt.key ? '#fff3e0' : 'transparent'
              }}
            >
              <i className={`fas ${opt.icon}`} style={{ width: 16 }}></i>
              {opt.label}
            </div>
          ))}
        </div>
      </Popover>
    </div>
  </div>

      <style>{`
    @keyframes postPulse {
      0% { box-shadow: 0 0 0 0 rgba(255,111,0,0.55); }
      70% { box-shadow: 0 0 0 10px rgba(255,111,0,0); }
      100% { box-shadow: 0 0 0 0 rgba(255,111,0,0); }
    }
    .post-affiliate-blink {
      animation: postPulse 1.8s infinite;
    }
  `}</style>
  <Button
    variant="contained"
    size="small"
    className="post-affiliate-blink"
    style={{ background: '#ff6f00', color: '#fff', textTransform: 'none', borderRadius: 20, fontWeight: 600 }}
    startIcon={<i className="fas fa-bullhorn"></i>}
    onClick={() => navigate({ targetRoute: '/provider/manageitems', targetTitle: 'Manage Items', targetData: {}, sourceState: {}, eventSource: '' })}
  >
    List for Affiliates
  </Button>
</div>


      {/* Promote Dialog */}
      <Dialog open={promoteDialogOpen} onClose={closePromoteDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Promote &amp; Earn</DialogTitle>
        <DialogContent>
          <div className="pb-2 font-14 text-muted">
            {selectedItem?.itemName ?? selectedItem?.ItemName}
          </div>
          <FormControlLabel
            control={<Checkbox checked={commissionChoice === 'default'} onChange={() => setCommissionChoice('default')} color="primary" />}
            label={`Accept existing commission (${Number(selectedItem?.marginNumber ?? selectedItem?.MarginNumber) || 0}%)`}
          />
          <div>
            <FormControlLabel
              control={<Checkbox checked={commissionChoice === 'custom'} onChange={() => setCommissionChoice('custom')} color="primary" />}
              label="Propose your own commission"
            />
          </div>
          {commissionChoice === 'custom' && (
                        <TextField
              type="number" variant="outlined" size="small" label="Commission %"
              value={customCommission}
              onChange={(e) => {
                const val = e.target.value;
                if (val === '' || (Number(val) >= 0 && Number(val) <= 100)) {
                  setCustomCommission(val);
                }
              }}
              inputProps={{ min: 0, max: 100 }}
              fullWidth className="mt-2"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closePromoteDialog} disabled={promoteSubmitting}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={submitPromotion} disabled={promoteSubmitting}>
            {promoteSubmitting ? 'Submitting...' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>

            {/* Product Preview Dialog */}
      <Dialog open={Boolean(previewItem)} onClose={() => setPreviewItem(null)} maxWidth="xs" fullWidth>
        {previewItem && (() => {
          const pImg = previewItem.imagePath ?? previewItem.ImagePath ?? '';
          const pName = previewItem.itemName ?? previewItem.ItemName ?? 'Product';
                    const pStall = getStallName(previewItem);
          const pCat = getCategoryName(previewItem);
          const pSlug = getStallSlug(previewItem);
          return (
            <>
              <DialogTitle>{pName}</DialogTitle>
              <DialogContent>
                <div style={{ height: 200, background: '#f5f6f8', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 8, marginBottom: 12 }}>
                  {pImg && pImg !== 'null' ? (
                    <img src={general.getImageUrl(pImg)} alt={pName} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                  ) : (
                    <i className="fas fa-image" style={{ fontSize: 32, color: '#cfd8dc' }}></i>
                  )}
                </div>
                                <div style={{ fontSize: 14, lineHeight: 2 }}>
                  <div><b>Stall:</b> {pStall || '—'}</div>

                  <div><b>Category:</b> {pCat || '—'}</div>
                </div>
              </DialogContent>
              <DialogActions>
                {pSlug && (
                  <Button
                    onClick={() => {
                      setPreviewItem(null);
                      navigate({ targetRoute: '/stalls/' + pSlug, targetTitle: 'Stall', targetData: {}, sourceState: {}, eventSource: '' });
                    }}
                    style={{ textTransform: 'none', color: '#ff6f00' }}
                  >
                    Visit Stall
                  </Button>
                )}
                <Button onClick={() => setPreviewItem(null)}>Close</Button>
              </DialogActions>
            </>
          );
        })()}
      </Dialog>

      {/* Image Viewer Dialog */}
      <Dialog
        open={imageViewerOpen}
        onClose={() => setImageViewerOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ style: { background: '#1e1e1e', borderRadius: '8px', overflow: 'hidden' } }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '8px 14px',
            background: '#2b2b2b'
          }}
        >
          <span style={{ color: '#fff', fontSize: '14px', fontWeight: 600 }}>Image Preview</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Button style={{ color: '#fff', minWidth: '40px' }} onClick={() => setZoomLevel(z => Math.max(0.5, z - 0.25))}>
              <i className="fas fa-search-minus"></i>
            </Button>
            <span style={{ color: '#bbb', fontSize: '12px', minWidth: '45px', textAlign: 'center' }}>
              {Math.round(zoomLevel * 100)}%
            </span>
            <Button style={{ color: '#fff', minWidth: '40px' }} onClick={() => setZoomLevel(z => Math.min(4, z + 0.25))}>
              <i className="fas fa-search-plus"></i>
            </Button>
            <Button style={{ color: '#fff', minWidth: '40px' }} onClick={() => setZoomLevel(1)}>
              <i className="fas fa-sync-alt"></i>
            </Button>
            <Button style={{ color: '#fff', minWidth: '40px' }} onClick={() => setImageViewerOpen(false)}>
              <i className="fas fa-times"></i>
            </Button>
          </div>
        </div>
        <div
          style={{
            height: '75vh',
            overflow: 'auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#1e1e1e'
          }}
        >
          <img
            src={viewerImage}
            alt="Preview"
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
              transition: 'transform 0.2s ease',
              transform: `scale(${zoomLevel})`,
              cursor: zoomLevel > 1 ? 'zoom-out' : 'zoom-in'
            }}
          />
        </div>
      </Dialog>

      {/* Stock Popover */}
      <Popover
        open={Boolean(stockPopupAnchor)}
        anchorEl={stockPopupAnchor}
        onClose={() => setStockPopupAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <div style={{ padding: '12px', minWidth: '160px' }}>
          <Typography variant="subtitle2" className="mb-1">Stock Info</Typography>
          <Typography variant="body2">
            {stockInfo ? (
              <>Available Quantity: <b>{stockInfo.currentQuantity}</b></>
            ) : (
              'Loading...'
            )}
          </Typography>
        </div>
      </Popover>

            {/* Product Grid */}
            <div className="affiliate-grid-container">

        {currentItems.length > 0 ? (
          <>
            <div className="affiliate-product-grid">
              {currentItems.map((item: any) => {
                const itemId = item.id ?? item.Id ?? item.stallItemId ?? item.StallItemId;
                const imagePath = item.imagePath ?? item.ImagePath ?? '';
                const itemName = item.itemName ?? item.ItemName ?? 'Product';
                const price = item.price ?? item.Price ?? item.sellingPrice ?? item.SellingPrice ?? 0;
                const margin = item.marginNumber ?? item.MarginNumber ?? 0;
                const quantity = item.availableQty ?? item.AvailableQty ?? item.currentQuantity ?? item.CurrentQuantity ?? item.quantity ?? item.Quantity ?? 0;
                                                                const itemOwnerId = item.ownerId ?? item.OwnerId ?? 0;
                const isOwnProduct = itemOwnerId === currentUserId;
                                // Guard against stale "Accepted"/"Pending" request records that outlive
                // the merchant removing the item from affiliate. allItems only ever
                // contains items that are CURRENTLY affiliate-enabled, so if this item
                // isn't in the current grid data at all, any old request for it is stale.
                const isStillLiveAffiliateItem = allItems.some((i: any) => (i.id ?? i.Id ?? i.stallItemId ?? i.StallItemId) === itemId);
                const myRequest = myRequestsByItemId[String(itemId)];
                const myRequestStatus = (myRequest && isStillLiveAffiliateItem) ? (myRequest.requestStatus || myRequest.RequestStatus) : null;
                const isPromoted = isStillLiveAffiliateItem && (promotedIds.includes(itemId) || myRequestStatus === 'Pending' || myRequestStatus === 'Accepted');

                                let cooldownHoursLeft = 0;
                if (myRequestStatus === 'Rejected') {
                  const modDate = myRequest.modifiedDate || myRequest.ModifiedDate;
                  if (modDate) {
                    const isoWithZ = /Z|[+-]\d{2}:\d{2}$/.test(modDate) ? modDate : modDate + 'Z';
                    const availableAt = new Date(isoWithZ).getTime() + 24 * 60 * 60 * 1000;
                    cooldownHoursLeft = Math.max(0, Math.ceil((availableAt - Date.now()) / (60 * 60 * 1000)));
                  }
                }
                const inCooldown = myRequestStatus === 'Rejected' && cooldownHoursLeft > 0;

                return (
                  <Card
  key={itemId}
  className="product-card-box"
  elevation={1}
  style={isOwnProduct ? { opacity: 0.65, filter: 'grayscale(10%)' } : undefined}
>
                    <div
                      className="product-img-wrapper pointer"
                      onClick={() => {
                        if (imagePath && imagePath !== 'null') {
                          setViewerImage(general.getImageUrl(imagePath));
                          setZoomLevel(1);
                          setImageViewerOpen(true);
                        }
                      }}
                                            title="Click to view full image"
                      style={{ height: '170px', background: '#f5f6f8' }}
                    >
                      {imagePath && imagePath !== 'null' ? (
                       <img src={general.getImageUrl(imagePath)} alt={itemName} className="grid-item-image" loading="lazy"
  style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                      ) : (
                        <div className="grid-no-image"><i className="fas fa-image"></i></div>
                      )}
                                                                 {myRequestStatus === 'Accepted' && (
                        <span className="promoted-badge" style={{ background: '#e8f5e9', color: '#2e7d32' }}>
                          <i className="fas fa-check-circle mr-1"></i>Accepted
                        </span>
                      )}
                      {myRequestStatus === 'Pending' && (
                        <span className="promoted-badge">
                          <i className="fas fa-clock mr-1"></i>Pending Approval
                        </span>
                      )}
                                            {myRequestStatus === 'Rejected' && (
                        <span className="promoted-badge" style={{ background: '#ffebee', color: '#c62828' }}>
                          <i className="fas fa-times-circle mr-1"></i>Rejected
                        </span>
                      )}

                    </div>

                    <CardContent className="product-card-content">
                                                                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                        <div className="product-title" title={itemName} style={{ flex: 1, minWidth: 0 }}>{itemName}</div>
                        <span
                          className="pointer"
                          title="Preview"
                          onClick={() => setPreviewItem(item)}
                          style={{
                            flexShrink: 0, width: 28, height: 28, borderRadius: '50%',
                            border: '1px solid #ffd3a6', color: '#ff6f00', background: '#fff',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 12, cursor: 'pointer'
                          }}
                        >
                          <i className="fas fa-eye"></i>
                        </span>
                      </div>
                      <div className="product-price-row">
                       <span className="product-price">₹{price}<small>/{item.unit ?? item.Unit ?? 'Unit'}</small></span>
                        <span className="product-qty">Qty: <b>{quantity}</b></span>
                      </div>

                      <div className="product-margin-row">
                        <TextField
                          type="number"
                          variant="outlined"
                          size="small"
                          label="Your Margin %"
                          value={margin}
                          className={classes.marginInput}
                          InputProps={{ readOnly: true }}
                        />
                      </div>

                                                             <div className="product-actions" style={{ minHeight: '44px' }}>
  {isOwnProduct ? (
    <div
      className="font-12 text-center py-2"
      style={{ color: '#9e9e9e', fontWeight: 600 }}
      title="You cannot promote your own product"
    >
      <i className="fas fa-ban mr-1"></i>Not eligible for self-promotion
    </div>
    ) : inCooldown ? (
    <div
      className="font-12 text-center py-2"
      style={{ color: '#c62828', fontWeight: 600 }}
      title="Rejected — you can try again in 24 hours"
    >
      <i className="fas fa-clock mr-1"></i>Try again in {cooldownHoursLeft}h
    </div>
  ) : myRequestStatus === 'Rejected' ? (
    <Button
      variant="contained"
      size="small"
      fullWidth
      className="promote-btn"
      onClick={() => openPromoteDialog(item)}
      startIcon={<i className="fas fa-redo"></i>}
    >
      Rejected — Try Again
    </Button>
    ) : isPromoted ? (
    <div
      className="font-12 text-center py-2"
      style={{ color: myRequestStatus === 'Accepted' ? '#2e7d32' : '#ef6c00', fontWeight: 600 }}
    >
      <i className={myRequestStatus === 'Accepted' ? 'fas fa-check-circle mr-1' : 'fas fa-clock mr-1'}></i>
      {myRequestStatus === 'Accepted' ? 'Added to your promotions' : 'Waiting for approval'}
    </div>
  ) : (
    <Button
      variant="contained"
      size="small"
      fullWidth
      className="promote-btn"
      onClick={() => openPromoteDialog(item)}
      startIcon={<i className="fas fa-bullhorn"></i>}
    >
            Promote &amp; Earn
    </Button>
  )}

</div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Pagination */}
                        {sourceItems.length > ITEMS_PER_PAGE && (
              <div className="pagination-row">
                <span className="text-muted font-13">
                  Showing {startIndex + 1} - {Math.min(startIndex + ITEMS_PER_PAGE, sourceItems.length)} of {sourceItems.length}
                </span>
                <div className="d-flex align-items-center">
                  <Button variant="outlined" size="small" className="mr-2" disabled={currentPage === 1} onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}>
                    Previous
                  </Button>
                  <span className="mx-2 font-weight-bold font-13">Page {currentPage} of {totalPages}</span>
                  <Button variant="outlined" size="small" className="ml-2" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}>
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="py-5 text-center text-muted">
            <i className="fas fa-box-open fa-2x mb-3 d-block"></i>
            No Records Found
          </div>
        )}
      </div>
    </div>
  );
};

export default AffiliateMarketingGrid;
